const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
const SQUEEZE_QUERY = window.matchMedia('(min-width: 1024px)');

/**
 * Returns the current page scroll container.
 * In the squeeze layout (desktop ≥1024px), `.page-wrapper` is the scroll container instead of
 * `document.scrollingElement`. On mobile, the document root scrolls natively so the address bar
 * can hide/show.
 *
 * @returns {Element} The scroll container element
 */
function getScrollContainer() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document.scrollingElement ?? document.documentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * Returns the current scroll position of the page scroll container.
 *
 * @returns {number} The scrollTop value
 */
function getScrollTop() {
  return getScrollContainer().scrollTop;
}

/**
 * Scrolls the page scroll container to the specified position.
 *
 * @param {ScrollToOptions} options - The scroll options (top, left, behavior)
 */
function scrollTo(options) {
  getScrollContainer().scrollTo(options);
}

/**
 * Manual scroll restoration for `.page-wrapper` as scroll container.
 *
 * On desktop (≥1024px), the browser restores scroll on `document.scrollingElement`, which
 * has `overflow: hidden` in the squeeze layout and can never scroll. We disable native
 * restoration and handle it via `history.state` instead.
 *
 * On mobile (<1024px), `document.scrollingElement` IS the scroll container, so native
 * restoration works perfectly — leave it enabled to avoid a one-frame flash from rAF.
 *
 * Save: `pagehide` — fires exactly once per navigation, captures the exact scroll position
 * at the moment the user leaves. More accurate than debounced scroll (which can be stale
 * if the user scrolls and immediately clicks a link).
 *
 * Restore: unconditional `pageshow` — Horizon uses cross-document view transitions, so
 * `pageshow` fires on every navigation (both bfcache and fresh loads). `popstate` is not
 * used because it doesn't fire for cross-document back navigation.
 */
if (SQUEEZE_QUERY.matches) {
  history.scrollRestoration = 'manual';
}

SQUEEZE_QUERY.addEventListener('change', () => {
  history.scrollRestoration = SQUEEZE_QUERY.matches ? 'manual' : 'auto';
});

/** How long a restore keeps retrying while the page is still streaming in. */
const RESTORE_TIMEOUT_MS = 1500;

/**
 * True while a restore is re-applying, so its own scrolls are not saved back.
 *
 * Starts true: on a back navigation the browser performs its own scroll restoration before this
 * module's `pageshow` handler runs, and it clamps against a page that has not finished streaming.
 * Measured coming back to a PLP saved at 900: the browser landed on 353 at t=352ms and `scrollend`
 * wrote that 353 over the saved 900, so the restore at t=468ms then had nothing better to apply.
 * Saving stays blocked until the restore below has settled.
 */
let isRestoringScroll = true;

/**
 * Saves the current scroll position into the current history entry.
 */
function saveScrollPosition() {
  try {
    const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
    history.replaceState({ ...currentState, scrollTop: getScrollContainer().scrollTop }, '');
  } catch (_) {
    // replaceState can throw if the state object exceeds the browser's size limit
  }
}

window.addEventListener('pagehide', saveScrollPosition);

/**
 * Keep the saved position in step with where the page actually is, not just where it was the
 * last time the page was left.
 *
 * `pagehide` alone stranded a stale value. Scrolling back to the top does not navigate, so
 * nothing rewrote `history.state.scrollTop` — it still held the offset saved when the page was
 * last left (e.g. 900 from clicking into a product). Reloading from the top then restored 900 and
 * dropped the shopper into the middle of the grid. `pagehide` does fire on reload and does save
 * 0, but too late: the browser has already snapshotted the entry's state for the new document.
 *
 * `scrollend` fires once per gesture, so it costs one write per scroll rather than one per frame.
 * Where it is unsupported a debounce stands in — 500ms keeps this clear of Safari's ~100
 * replaceState calls per 30s. Both paths skip the write when the value has not moved.
 */
let pendingScrollSave = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
let lastSavedScrollTop = /** @type {number | null} */ (null);

function saveScrollPositionIfMoved() {
  if (!SQUEEZE_QUERY.matches) return;

  // `scrollend` fires for programmatic scrolls too, so without this the restore's own intermediate
  // (still-clamped) offsets would be written back as the saved position and become sticky.
  if (isRestoringScroll) return;

  const scrollTop = getScrollContainer().scrollTop;
  if (scrollTop === lastSavedScrollTop) return;

  lastSavedScrollTop = scrollTop;
  saveScrollPosition();
}

if ('onscrollend' in window) {
  // Capture: scroll events do not bubble, but they do reach ancestors in the capture phase, and
  // the scroll container is `.page-wrapper` on desktop and the document on mobile.
  document.addEventListener('scrollend', saveScrollPositionIfMoved, { capture: true, passive: true });
} else {
  document.addEventListener(
    'scroll',
    () => {
      if (pendingScrollSave) clearTimeout(pendingScrollSave);
      pendingScrollSave = setTimeout(() => {
        pendingScrollSave = null;
        saveScrollPositionIfMoved();
      }, 500);
    },
    { capture: true, passive: true }
  );
}

/**
 * Restores a saved scroll position onto the current scroll container.
 *
 * @param {number} savedScrollTop
 */
function restoreSavedScrollTop(savedScrollTop) {
  if (!Number.isFinite(savedScrollTop) || savedScrollTop < 0) return;

  const container = getScrollContainer();

  // One rAF is too early on a collection page: ~1MB of HTML is still streaming, so the container
  // has not reached its full height and `Math.min(saved, max)` clamped the target to whatever had
  // rendered — coming back to a PLP scrolled to 900 landed at 353. Keep re-applying until the
  // container is tall enough to honour the saved offset, or until the deadline, so a page that
  // legitimately got shorter still settles instead of spinning.
  const deadline = performance.now() + RESTORE_TIMEOUT_MS;

  isRestoringScroll = true;
  restoreSuperseded = false;

  const attempt = () => {
    if (restoreSuperseded) return;

    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);

    // Use scrollTo with 'instant' to override CSS scroll-behavior: smooth on .page-wrapper
    container.scrollTo({ top: Math.min(savedScrollTop, maxScrollTop), behavior: 'instant' });

    if (maxScrollTop < savedScrollTop && performance.now() < deadline) {
      requestAnimationFrame(attempt);
      return;
    }

    isRestoringScroll = false;
  };

  attempt();
}

let restoreSuperseded = false;

const ANCHOR_SETTLE_MS = 1500;
/** Card heights above the anchor were still settling 2.7s after Back in WebKit mobile. */
const ANCHOR_QUIET_MS = 800;
const ANCHOR_MAX_MS = 4000;
const SCROLL_ANCHORS_KEY = 'scroll-anchors';
const SCROLL_ENTRY_KEY = 'scrollEntry';
const MAX_SCROLL_ANCHORS = 40;

/** Keyed without `page`: Back to a paginated list can render a different page of the same list. */
function scrollAnchorKey() {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.delete('page');
  return url.pathname + url.search;
}

/** @returns {Record<string, { id: string, top: number }>} */
function readScrollAnchors() {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_ANCHORS_KEY) ?? '{}') ?? {};
  } catch (_) {
    return {};
  }
}

/** @param {Record<string, { id: string, top: number }>} anchors */
function writeScrollAnchors(anchors) {
  const keys = Object.keys(anchors);
  for (const key of keys.slice(0, Math.max(0, keys.length - MAX_SCROLL_ANCHORS))) delete anchors[key];

  try {
    sessionStorage.setItem(SCROLL_ANCHORS_KEY, JSON.stringify(anchors));
  } catch (_) {
    // Storage can be full or disabled; the offset restore still runs.
  }
}

/**
 * Tags each history entry, so two entries for the same list (a breadcrumb back to it, say) keep
 * separate anchors. Set outside `pagehide`, which is the only time WebKit keeps it.
 *
 * @returns {string | undefined}
 */
function scrollEntry() {
  return typeof history.state === 'object' && history.state !== null ? history.state[SCROLL_ENTRY_KEY] : undefined;
}

/**
 * Remembers where `element` sits in the viewport, for the next Back or reload of this page.
 * sessionStorage, not `history.state`: WebKit drops a `replaceState` made during `pagehide`.
 *
 * @param {HTMLElement | null} element - `null` forgets the anchor for this page.
 */
function saveScrollAnchor(element) {
  const anchors = readScrollAnchors();
  const key = scrollAnchorKey();
  const entry = scrollEntry();

  delete anchors[key];
  if (entry) delete anchors[`${key}#${entry}`];

  if (element?.id) {
    const anchor = { id: element.id, top: element.getBoundingClientRect().top };
    // By URL too: WebKit can come back from a Back that missed the bfcache with no `history.state`.
    anchors[key] = anchor;
    if (entry) anchors[`${key}#${entry}`] = anchor;
  }

  writeScrollAnchors(anchors);
}

/** @returns {{ id: string, top: number } | null} */
function takeScrollAnchor() {
  const anchors = readScrollAnchors();
  const entry = scrollEntry();
  const key = entry ? `${scrollAnchorKey()}#${entry}` : scrollAnchorKey();
  const anchor = anchors[key];
  if (!anchor) return null;

  delete anchors[key];
  writeScrollAnchors(anchors);
  return anchor;
}

const navigationType = /** @type {PerformanceNavigationTiming | undefined} */ (
  performance.getEntriesByType?.('navigation')[0]
)?.type;

/** Held until its element exists: it can sit on a page that paginated-list re-renders after load. */
let pendingScrollAnchor = navigationType === 'back_forward' || navigationType === 'reload' ? takeScrollAnchor() : null;

if (!scrollEntry()) {
  try {
    const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
    history.replaceState({ ...state, [SCROLL_ENTRY_KEY]: Math.random().toString(36).slice(2) }, '');
  } catch (_) {
    // replaceState can throw if the state object exceeds the browser's size limit
  }
}

let shopperHasScrolled = false;

// Once the shopper scrolls, an anchor that only turns up later must not yank them back.
for (const type of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
  window.addEventListener(
    type,
    () => {
      shopperHasScrolled = true;
      pendingScrollAnchor = null;
    },
    { capture: true, passive: true }
  );
}

/** @returns {boolean} */
function hasPendingScrollAnchor() {
  return pendingScrollAnchor !== null;
}

/** @type {{ element: Element, top: number, deadline: number, cap: number } | null} */
let activePin = null;

/**
 * Keeps `element` at viewport offset `top` until the page settles, or until the shopper scrolls.
 * One pin at a time, so two loops never pull the page in different directions.
 *
 * @param {Element} element
 * @param {number} top
 */
function pinElement(element, top) {
  const running = activePin !== null;
  const now = performance.now();
  activePin = { element, top, deadline: now + ANCHOR_SETTLE_MS, cap: now + ANCHOR_MAX_MS };
  applyPin(activePin);
  if (running) return;

  const settle = () => {
    const pin = activePin;
    if (!pin || shopperHasScrolled || !pin.element.isConnected || performance.now() > pin.deadline) {
      activePin = null;
      return;
    }

    // Held until the layout has been quiet for a moment, not for a fixed time.
    if (applyPin(pin)) pin.deadline = Math.max(pin.deadline, Math.min(pin.cap, performance.now() + ANCHOR_QUIET_MS));
    requestAnimationFrame(settle);
  };

  requestAnimationFrame(settle);
}

/**
 * @param {{ element: Element, top: number }} pin
 * @returns {boolean} Whether the page had moved and was scrolled back.
 */
function applyPin({ element, top }) {
  const shift = element.getBoundingClientRect().top - top;
  if (Math.abs(shift) < 1) return false;

  const container = getScrollContainer();
  container.scrollTo({ top: container.scrollTop + shift, behavior: 'instant' });
  return true;
}

/**
 * After an insert above `element`, holds the view until the shopper scrolls: the new cards keep
 * settling in height, which WebKit does not anchor.
 *
 * @param {Element} element - What should stay put if nothing is pinned yet.
 */
function holdScrollPosition(element) {
  if (shopperHasScrolled) return;
  if (!activePin) return pinElement(element, element.getBoundingClientRect().top);

  const now = performance.now();
  activePin.deadline = now + ANCHOR_SETTLE_MS;
  activePin.cap = now + ANCHOR_MAX_MS;
}

/**
 * An offset cannot survive a paginated grid: back on `?page=3` only page 3 renders, so the saved
 * offset lands in the footer. The saved anchor element is put back at its old viewport offset.
 *
 * @returns {boolean} Whether the anchor was found and applied.
 */
function restoreScrollAnchor() {
  const anchor = pendingScrollAnchor;
  const element = anchor?.id ? document.getElementById(anchor.id) : null;
  if (!anchor || !element || !Number.isFinite(anchor.top)) return false;

  pendingScrollAnchor = null;

  // Re-pinned while cards above it settle in height after load.
  pinElement(element, anchor.top);

  restoreSuperseded = true;
  isRestoringScroll = false;
  return true;
}

/**
 * Applies the position held in the current history entry, if it has one.
 *
 * @param {{ defer?: boolean }} [options] - `defer: false` applies it in this task rather than on
 *   the next frame, so the page can be placed before its first paint.
 */
function restoreFromHistoryState({ defer = true } = {}) {
  if (restoreScrollAnchor()) return;

  const scrollTop = history.state?.scrollTop;

  // Nothing to restore: release the save block so ordinary scrolling is recorded again.
  if (scrollTop == null) {
    isRestoringScroll = false;
    return;
  }

  if (!defer) {
    restoreSavedScrollTop(scrollTop);
    return;
  }

  requestAnimationFrame(() => {
    restoreSavedScrollTop(scrollTop);
  });
}

/**
 * A cross-document back navigation re-runs this module as an ordinary deferred module script,
 * which is the earliest the position can be applied — the document is parsed, so the container
 * exists and already has most of its height.
 *
 * `pageshow` is far too late for that case: it waits on `load`, i.e. every product image on the
 * grid. Measured coming back to a PLP saved at 2400, the page painted at the top and stayed there
 * for 1.4s before snapping down — the visible "jump" at the product cards. `restoreSavedScrollTop`
 * re-applies until the container is tall enough, so running it here costs nothing when the grid is
 * still streaming — and applying it in this task rather than on the next frame gets it in ahead of
 * the first paint.
 */
restoreFromHistoryState({ defer: false });

window.addEventListener('pageshow', (event) => {
  // A bfcache restore does not re-execute the module, so that path still needs the event. A fresh
  // load has already been handled above, and redoing it here would yank a shopper who has started
  // scrolling in the meantime.
  if (!event.persisted) return;

  shopperHasScrolled = false;
  pendingScrollAnchor = takeScrollAnchor();
  restoreFromHistoryState();
});

/**
 * Same-document scroll restoration for pushState navigation.
 *
 * `pagehide`/`pageshow` only fire for cross-document navigations. For same-document
 * pushState navigation (filters, pagination), we patch pushState to auto-save scroll
 * position before each new entry, and restore on popstate (back/forward).
 */
const originalPushState = history.pushState.bind(history);
history.pushState = function (state, title, url) {
  // Not while restoring. A filtered PLP pushes state during load (the facets component
  // normalises the URL), which otherwise wrote the browser's early clamped offset over the
  // position actually being restored — coming back to a grid saved at 900 stuck at 353.
  if (!isRestoringScroll) saveScrollPosition();

  originalPushState(state, title, url);
};

window.addEventListener('popstate', () => {
  const scrollTop = history.state?.scrollTop;
  if (scrollTop == null) return;

  requestAnimationFrame(() => {
    restoreSavedScrollTop(scrollTop);
  });
});

/**
 * Returns the appropriate target for listening to scroll events.
 * On desktop (≥1024px), `.page-wrapper` emits scroll events directly.
 * On mobile, the document root scrolls natively and scroll events bubble to `document`.
 *
 * @returns {EventTarget} The target to call addEventListener('scroll', ...) on
 */
function getScrollEventTarget() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document;
  }
  return document;
}

/**
 * Returns the appropriate root for an IntersectionObserver monitoring the scroll container.
 * On desktop (≥1024px), `.page-wrapper` must be set as the explicit root.
 * On mobile, `null` uses the viewport root (the IntersectionObserver default).
 *
 * @returns {Element | null} The root option for IntersectionObserver
 */
function getIntersectionRoot() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? null;
  }
  return null;
}

export {
  getScrollContainer,
  getScrollTop,
  scrollTo,
  getScrollEventTarget,
  getIntersectionRoot,
  hasPendingScrollAnchor,
  holdScrollPosition,
  restoreScrollAnchor,
  saveScrollAnchor,
  SQUEEZE_QUERY as scrollContainerMediaQuery,
};
